import { Controller } from '@hotwired/stimulus';

/**
 * Resend OTP Controller
 *
 * Handles the countdown timer for resending OTP codes:
 * - Shows countdown timer initially (30 seconds)
 * - Hides timer and shows "Send again" link when countdown reaches 0
 * - Restarts countdown when "Send again" is clicked
 * - Prevents clicking the resend link during countdown
 */
export default class extends Controller {
  static targets = ['action', 'message', 'timer', 'status'];
  static values = {
    duration: { type: Number, default: 30 },
    url: { type: String, default: '' }
  };

  connect() {
    console.log('Resend OTP controller connected');
    this.timeRemaining = this.durationValue;
    this.intervalId = null;

    // Start the initial countdown
    this.startCountdown();
  }

  disconnect() {
    // Clean up the interval when controller disconnects
    this.stopCountdown();
  }

  startCountdown() {
    // Stop any existing countdown
    this.stopCountdown();

    // Reset time remaining
    this.timeRemaining = this.durationValue;

    // Show message, hide action
    this.showMessage();
    this.hideAction();

    // Update the timer display immediately
    this.updateTimerDisplay();

    // Start the countdown interval
    this.intervalId = setInterval(() => {
      this.timeRemaining--;

      if (this.timeRemaining <= 0) {
        this.stopCountdown();
        this.showAction();
        this.hideMessage();
      } else {
        this.updateTimerDisplay();
      }
    }, 1000);
  }

  stopCountdown() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  updateTimerDisplay() {
    if (!this.hasTimerTarget) return;

    // Format time as "XX seconds" or "1 second"
    const seconds = this.timeRemaining;
    const text = seconds === 1 ? '1 second' : `${seconds} seconds`;
    this.timerTarget.textContent = text;
  }

  showAction() {
    if (this.hasActionTarget) {
      this.actionTarget.classList.remove('hidden');
    }
  }

  hideAction() {
    if (this.hasActionTarget) {
      this.actionTarget.classList.add('hidden');
    }
  }

  showMessage() {
    if (this.hasMessageTarget) {
      this.messageTarget.classList.remove('hidden');
    }
  }

  hideMessage() {
    if (this.hasMessageTarget) {
      this.messageTarget.classList.add('hidden');
    }
  }

  async resend(event) {
    event.preventDefault();

    // Don't allow resending during countdown
    if (this.intervalId) {
      return;
    }

    console.log('Resending OTP...');

    // If URL is provided, make a request to resend the OTP
    if (this.urlValue) {
      try {
        // Show loading state (optional)
        const originalText = this.actionTarget.textContent;
        this.actionTarget.textContent = 'Sending...';
        this.actionTarget.classList.add('opacity-50', 'pointer-events-none');

        const response = await fetch(this.urlValue, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRFToken': this.getCsrfToken(),
          },
        });

        // Restore original text
        this.actionTarget.textContent = originalText;
        this.actionTarget.classList.remove('opacity-50', 'pointer-events-none');

        if (response.ok) {
          console.log('OTP resent successfully');
          // Show success message (optional)
          this.showSuccessMessage();
        } else {
          console.error('Failed to resend OTP');
          // Show error message (optional)
          this.showErrorMessage();
          return; // Don't restart countdown on error
        }
      } catch (error) {
        console.error('Error resending OTP:', error);
        this.actionTarget.classList.remove('opacity-50', 'pointer-events-none');
        this.showErrorMessage();
        return; // Don't restart countdown on error
      }
    }

    // Restart the countdown
    this.startCountdown();
  }

  getCsrfToken() {
    // Get CSRF token from cookie or meta tag
    const tokenElement = document.querySelector('[name=csrfmiddlewaretoken]');
    if (tokenElement) {
      return tokenElement.value;
    }

    // Fallback to cookie
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  announce(text) {
    if (!this.hasStatusTarget) return;
    // Clear first so re-sending the same message is re-announced.
    this.statusTarget.textContent = '';
    // Next tick so assistive tech registers the text change as an update.
    setTimeout(() => {
      this.statusTarget.textContent = text;
    }, 50);
  }

  showSuccessMessage() {
    this.announce('A new verification code has been sent.');
  }

  showErrorMessage() {
    this.announce('Could not resend the code. Please try again.');
  }

  // Public method to get remaining time
  getRemainingTime() {
    return this.timeRemaining;
  }

  // Public method to check if countdown is active
  isCountdownActive() {
    return this.intervalId !== null;
  }
}
